import { useEffect, useRef, type ReactNode } from 'react'
import { Chevron, Kbd } from '../Page'

/**
 * The keyboard path, said in the product rather than only in the handbook.
 *
 * Every screen is completable without a pointer, and a shortcut nobody can
 * find is a shortcut nobody has. Regions are a single tab stop with arrow
 * traversal in reading order, so a keyboard user reaches region 6 of 13 in six
 * keystrokes rather than six tab stops.
 */

const ROWS: [ReactNode, string][] = [
  ['O', 'Overlay on / off'],
  [
    <span key="arrows" className="inline-flex items-center gap-0.5">
      <Chevron direction="left" size={11} />
      <Chevron direction="right" size={11} />
    </span>,
    'Previous / next page',
  ],
  ['N P', 'Next / previous search hit'],
  ['Tab', 'Region layer, then arrows'],
  ['S', 'Start a region selection'],
  ['C', 'Cite the selection'],
  ['L', 'Copy permalink'],
  ['R', 'Raw / corrected text'],
  ['+ - 0', 'Zoom in, out, fit'],
]

export function KeyboardPanel({ onClose }: { onClose: () => void }) {
  const panel = useRef<HTMLDivElement>(null)

  useEffect(() => {
    panel.current?.focus()
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onClose])

  return (
    <>
      <div
        className="fixed inset-0 z-40"
        style={{ background: 'color-mix(in srgb, var(--jl-well) 42%, transparent)' }}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-label="Keyboard path"
        className="popover fixed left-1/2 top-1/2 z-50 w-[380px] max-w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 overflow-hidden"
      >
        <div className="flex items-baseline justify-between border-b border-n300 px-4 py-3">
          <div className="chrome">Keyboard path</div>
          <button type="button" className="btn-ghost text-[13px]" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="flex flex-col">
          {ROWS.map(([key, label], index) => (
            <div
              key={index}
              className={`flex items-center gap-3 px-4 py-1.5 ${
                index ? 'border-t border-n200' : ''
              }`}
            >
              <div className="flex min-w-[52px] items-center gap-1">
                {typeof key === 'string' ? (
                  key.split(' ').map((one) => <Kbd key={one}>{one}</Kbd>)
                ) : (
                  <Kbd>{key}</Kbd>
                )}
              </div>
              <div className="text-[13.5px] text-ink2">{label}</div>
            </div>
          ))}
        </div>
        <p className="border-t border-n300 px-4 py-3 text-[13.5px] leading-normal text-ink2">
          Tab order runs chrome, then the edition rail, then the page, then the panel. The region
          layer is one tab stop; the arrows move through the regions in reading order.
        </p>
      </div>
    </>
  )
}
